import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, toast } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi, type DisplayCollectionDTO, type DisplayCollectionImageLineDTO } from "../../api";
import { OWNER_VARIANT } from "../../components/badges";
import { CopyUrl, EnumBadge, lineImage, objectUrl, Thumbnail } from "../../components/cells";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";

/**
 * The images inside one display collection, in the order the buyer will see
 * them.
 *
 * Ordering and removal are local edits to the collection document — nothing
 * reaches the server until Save, which is what makes reordering a whole
 * gallery a single write rather than one per row.
 */
export function DisplayCollectionDetailPage() {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const { id } = useParams({ from: "/collection/display-collections/$id" });
  const collectionId = Number(id);
  const allowed = useAllowed(LogicView.COLLECTION_DISPLAY);
  const title = t("navigation.item.display-collections", "Display");

  const [draft, setDraft] = useState<DisplayCollectionDTO | null>(null);
  const [saving, setSaving] = useState(false);

  const collection = useQuery({
    queryKey: ["display-collection", collectionId],
    queryFn: () => api.displayCollections.findById(collectionId),
    enabled: allowed && Number.isFinite(collectionId),
  });

  useEffect(() => {
    if (collection.data) setDraft(structuredClone(collection.data));
  }, [collection.data]);

  const images = draft?.images ?? [];

  const cols = useMemo(() => {
    const c = defineColumns<DisplayCollectionImageLineDTO>();
    const typeOf = (row: DisplayCollectionImageLineDTO) =>
      row.owner === "ARTWORK"
        ? (row.artwork_image?.type ?? "")
        : (row.product_image?.type ?? "");
    return [
      c.custom(
        "thumbnail",
        () => "",
        (row) => <Thumbnail url={objectUrl(lineImage(row))} size="md" />,
        { headerKey: "column.thumbnail", filter: "none", enableSorting: false, size: 64 },
      ),
      c.custom(
        "owner",
        (row) => row.owner,
        (row) => <EnumBadge value={row.owner} variants={OWNER_VARIANT} size="sm" />,
        { headerKey: "column.owner", filter: "select", priority: 2 },
      ),
      c.text("type", typeOf, { headerKey: "column.type", priority: 3 }),
      c.custom(
        "url",
        (row) => lineImage(row)?.url ?? "",
        (row) => <CopyUrl url={lineImage(row)?.url} />,
        { headerKey: "column.url", filter: "none", priority: 4 },
      ),
    ];
  }, []);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      // Persist the visible order as the stored sort order.
      await api.displayCollections.update({
        ...draft,
        images: draft.images.map((line, index) => ({ ...line, sort_order: index })),
      });
      toast.success(t("notification.save.success", "Item saved successfully"));
      await queryClient.invalidateQueries({ queryKey: ["display-collection"] });
    } catch (failure) {
      toast.error(
        failure instanceof Error ? failure.message : t("notification.save.error", "Failed to save item"),
      );
    } finally {
      setSaving(false);
    }
  };

  if (!allowed) return <AccessDenied title={title} />;
  if (collection.error) return <ErrorState title={title} error={collection.error} />;

  return (
    <>
      <PageHeader
        parent={{ to: "/collection/display-collections", label: t("navigation.item.display-collections", "Display collections") }}
        title={`${draft?.name ?? `#${collectionId}`}`}
        badges={<Badge size="sm">{`${images.length}`}</Badge>}
        actions={
          <div className="flex items-center gap-2">
<Button
              size="sm"
              variant="success"
              loading={saving}
              icon={<Save className="size-3.5" />}
              onClick={() => void save()}
            >
              {t("button.save", "Save")}
            </Button>
          </div>
        }
      />

      <DataTable<DisplayCollectionImageLineDTO>
        data={images}
        columns={cols}
        getRowId={(row) => `${row.owner}-${row.image_id}`}
        loading={collection.isLoading}
        rowHeight={52}
        selectable
        onReorder={(next) => draft && setDraft({ ...draft, images: next })}
        onRowClick={(row) => {
          const url = objectUrl(lineImage(row));
          if (url) window.open(url, "_blank", "noreferrer");
        }}
        onDeleteSelected={(selection) => {
          if (!draft) return;
          const removed = new Set(selection.map((row) => `${row.owner}-${row.image_id}`));
          setDraft({
            ...draft,
            images: draft.images.filter((row) => !removed.has(`${row.owner}-${row.image_id}`)),
          });
        }}
        deleteSelectedConfirm={() => ({
          title: t("dialog.confirm.delete.title", "Confirm delete"),
          message: t(
            "dialog.confirm.delete.text",
            "Are you sure you want to delete the selected items?",
          ),
        })}
      />
    </>
  );
}
